import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Users, Sparkles, ShieldCheck, Globe2, Building2, Briefcase, Megaphone, ArrowDownCircle, Star, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { BusinessCard } from "@/components/Cards";
import { useRegisterModal } from "@/components/RegisterModal";
import api, { formatApiError } from "@/lib/api";

const HERO_IMG_1 = "https://images.unsplash.com/photo-1620039423059-58fe9a0ca20a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const HERO_IMG_2 = "https://images.unsplash.com/photo-1653656120824-af2432523c08?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const HERO_IMG_3 = "https://images.unsplash.com/photo-1573496267526-08a69e46a409?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const ABOUT_IMG = "https://images.unsplash.com/photo-1542323228-002ac256e7b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1698129180834-d8454d2c9608?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const Hero = () => {
  const { t } = useI18n();
  const { open: openRegister } = useRegisterModal();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    api.get("/public/counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, []);
  const taglineParts = t.hero.taglineParts || [];
  return (
    <section className="relative overflow-hidden bg-cream">
      {/* Organic decorative shapes */}
      <div aria-hidden className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-orange/10 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute top-1/3 -right-20 w-[420px] h-[420px] rounded-full bg-teal/10 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute inset-0 opacity-[0.035] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, #1F4E47 1px, transparent 0)",
        backgroundSize: "24px 24px",
      }} />

      <div className="relative container-tight py-16 md:py-24 lg:py-28">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
          {/* LEFT — Copy + CTAs */}
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 bg-white border border-orange/20 text-orange rounded-md px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] shadow-sm" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-orange animate-pulse"></span>
              {t.hero.eyebrow}
            </span>

            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight mt-5 text-teal-deep">
              {t.hero.title}
            </h1>

            {taglineParts.length > 0 && (
              <p className="mt-5 font-display text-2xl md:text-3xl leading-tight text-teal-deep">
                {taglineParts.map((part, i) => {
                  const colors = ["#E97A3A", "#1F4E47", "#E97A3A"];
                  return (
                    <React.Fragment key={part}>
                      <span
                        className="relative inline-block"
                        style={{
                          backgroundImage: `linear-gradient(180deg, transparent 62%, ${colors[i]}33 62%, ${colors[i]}33 92%, transparent 92%)`,
                          paddingInline: "2px",
                        }}
                      >
                        {part}
                      </span>
                      {i < taglineParts.length - 1 && <span className="text-orange mx-2">·</span>}
                    </React.Fragment>
                  );
                })}
              </p>
            )}

            <p className="mt-6 text-base md:text-lg text-teal-soft max-w-xl leading-relaxed">{t.hero.subtitle}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button type="button" onClick={openRegister} className="btn-orange" data-testid="hero-cta-register">
                {t.hero.ctaPrimary} <ArrowRight size={16} />
              </button>
              <Link to="/directory" className="btn-teal-outline" data-testid="hero-cta-directory">
                {t.hero.ctaSecondary} <ArrowRight size={14} />
              </Link>
            </div>

            {/* Trust row */}
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=80&q=80",
                  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=80&q=80",
                  "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=80&q=80",
                  "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=80&q=80",
                ].map((src, i) => (
                  <span
                    key={i}
                    className="w-9 h-9 rounded-full border-2 border-cream bg-center bg-cover"
                    style={{ backgroundImage: `url(${src})` }}
                    aria-hidden
                  />
                ))}
              </div>
              <div className="text-sm">
                <div className="flex items-center gap-1 text-orange">
                  {[0, 1, 2, 3, 4].map((i) => (<Star key={i} size={14} fill="currentColor" strokeWidth={0} />))}
                  <span className="text-teal-deep font-bold ml-1">4.9</span>
                </div>
                <p className="text-teal-soft text-xs mt-0.5" data-testid="hero-trust-line">
                  {counts ? `+${counts.members} ${t.hero.membersLabel}` : t.hero.membersLabelLoading}
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT — Photo mosaic + floating cards */}
          <div className="relative h-[420px] md:h-[520px] hidden lg:block">
            <div
              className="absolute top-0 left-0 w-[62%] h-[58%] rounded-2xl overflow-hidden shadow-xl bg-center bg-cover"
              style={{ backgroundImage: `url(${HERO_IMG_1})` }}
            />
            <div
              className="absolute top-[22%] right-0 w-[46%] h-[46%] rounded-2xl overflow-hidden shadow-2xl bg-center bg-cover ring-4 ring-cream"
              style={{ backgroundImage: `url(${HERO_IMG_2})` }}
            />
            <div
              className="absolute bottom-0 left-[16%] w-[58%] h-[42%] rounded-2xl overflow-hidden shadow-xl bg-center bg-cover ring-4 ring-cream"
              style={{ backgroundImage: `url(${HERO_IMG_3})` }}
            />

            {/* Floating stat card 1 */}
            <div className="absolute top-[6%] right-[2%] bg-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-float">
              <span className="w-10 h-10 rounded-lg bg-orange/10 text-orange flex items-center justify-center">
                <HeartHandshake size={18} />
              </span>
              <div>
                <div className="text-teal-deep font-display font-bold text-lg leading-none" data-testid="hero-businesses-count">
                  {counts ? `+${counts.businesses}` : "…"}
                </div>
                <div className="text-teal-soft text-[11px] uppercase tracking-wider mt-1">{t.hero.businessesLabel}</div>
              </div>
            </div>

            {/* Floating stat card 2 */}
            <div className="absolute bottom-[4%] right-[8%] bg-teal-deep text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-float-delay">
              <span className="w-10 h-10 rounded-lg bg-orange text-white flex items-center justify-center">
                <Check size={18} />
              </span>
              <div>
                <div className="font-display font-bold text-lg leading-none">Verificado</div>
                <div className="text-white/70 text-[11px] uppercase tracking-wider mt-1">100% humano</div>
              </div>
            </div>
          </div>

          {/* Mobile mini-mosaic below CTAs */}
          <div className="grid grid-cols-3 gap-2 lg:hidden">
            {[HERO_IMG_1, HERO_IMG_2, HERO_IMG_3].map((src) => (
              <div
                key={src}
                className="aspect-square rounded-xl bg-center bg-cover shadow-md"
                style={{ backgroundImage: `url(${src})` }}
              />
            ))}
          </div>
        </div>
      </div>

      <a href="#services" className="absolute bottom-5 left-1/2 -translate-x-1/2 text-teal-soft hover:text-orange transition-colors animate-bounce" aria-label="Scroll down">
        <ArrowDownCircle size={26} />
      </a>
    </section>
  );
};

const ServiceStrip = () => {
  const { t } = useI18n();
  const icons = [Users, Briefcase, Megaphone, Building2];
  return (
    <section id="services" className="bg-white border-b border-gray-100">
      <div className="container-tight grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 py-12">
        {t.services.items.map((s, i) => {
          const Icon = icons[i] || Users;
          return (
            <div key={s.t} className="flex items-center gap-4" data-testid={`service-${i}`}>
              <span className="w-14 h-14 rounded-full bg-orange flex items-center justify-center shrink-0 shadow-md shadow-orange/20">
                <Icon className="text-white" size={24} />
              </span>
              <div>
                <h3 className="font-display text-xl text-teal-deep leading-tight">{s.t}</h3>
                <p className="text-sm text-teal-soft">{s.d}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const WhyJoin = () => {
  const { t } = useI18n();
  const { open: openRegister } = useRegisterModal();
  const icons = [Sparkles, Megaphone, ShieldCheck, Globe2];
  return (
    <section className="section bg-cream">
      <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5">
          <p className="eyebrow text-orange">{t.why.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-3">{t.why.title}</h2>
          <p className="mt-5 text-teal-soft text-lg leading-relaxed">{t.why.subtitle}</p>
          <button type="button" onClick={openRegister} className="btn-orange mt-8" data-testid="why-cta">
            {t.why.cta} <ArrowRight size={16} />
          </button>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {t.why.bullets.map((b, i) => {
            const Icon = icons[i];
            return (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-teal transition-colors">
                <span className="w-11 h-11 rounded-full bg-teal-mist text-teal flex items-center justify-center">
                  <Icon size={20} />
                </span>
                <h3 className="font-display text-2xl text-teal-deep mt-4 leading-tight">{b.t}</h3>
                <p className="text-sm text-teal-soft mt-2 leading-relaxed">{b.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FeaturedDirectory = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/entrepreneurs/preview", { params: { limit: 6 } })
      .then(({ data }) => setItems(data.items))
      .catch((e) => console.error(formatApiError(e)));
  }, []);
  if (!items.length) return null;
  return (
    <section className="section bg-white">
      <div className="container-tight">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <p className="eyebrow text-orange">{t.directoryPreview.eyebrow}</p>
            <h2 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-3">{t.directoryPreview.title}</h2>
          </div>
          <Link to="/directory" className="btn-teal-outline" data-testid="featured-see-all">
            {t.directoryPreview.seeAll} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {items.map((b) => <BusinessCard key={b.id} biz={b} />)}
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const { t } = useI18n();
  return (
    <section id="about" className="section bg-teal-mist/40">
      <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="rounded-3xl overflow-hidden border-8 border-white shadow-xl">
            <img src={ABOUT_IMG} alt="" className="w-full h-[480px] object-cover" />
          </div>
        </div>
        <div className="lg:col-span-6">
          <p className="eyebrow text-orange">{t.nav.about}</p>
          <h2 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-3">{t.about.missionTitle}</h2>
          <p className="mt-5 text-teal-soft text-lg leading-relaxed italic font-display">{t.about.missionQuote}</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h3 className="font-display text-2xl text-teal">{t.about.missionEyebrow}</h3>
              <p className="text-sm text-teal-soft mt-2 leading-relaxed">{t.about.missionP1}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h3 className="font-display text-2xl text-teal">{t.about.visionEyebrow}</h3>
              <p className="text-sm text-teal-soft mt-2 leading-relaxed">{t.about.visionP1}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", role: "", contribution: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: "" });
    try {
      await api.post("/contact", form);
      setStatus({ loading: false, ok: true, error: "" });
      setForm({ name: "", email: "", role: "", contribution: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: formatApiError(err) });
    }
  };
  return (
    <section id="contact" className="section bg-white">
      <div className="container-tight grid grid-cols-1 lg:grid-cols-2 gap-14">
        <div>
          <p className="eyebrow text-orange">{t.contact.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-3">{t.contact.title}</h2>
          <p className="mt-4 text-teal-soft text-lg">{t.contact.subtitle}</p>
          <div className="mt-10 space-y-6 text-sm">
            <div>
              <h4 className="eyebrow text-teal">{t.contact.address}</h4>
              <p className="mt-1 text-teal-soft">1104 S Crane Ave<br />Odessa, TX</p>
            </div>
            <div>
              <h4 className="eyebrow text-teal">{t.contact.hours}</h4>
              <p className="mt-1 text-teal-soft">Monday – Friday · 8AM – 5PM</p>
            </div>
            <div>
              <h4 className="eyebrow text-teal">{t.contact.contacts}</h4>
              <p className="mt-1 text-teal-soft">info@lovesolidarity.com</p>
              <p className="mt-1 text-teal-soft">+1 (432) 338-2869</p>
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="bg-cream rounded-3xl p-8 border border-gray-200" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.contact.name}</label>
              <input className="field-input mt-1" required value={form.name} onChange={onChange("name")} data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.contact.email}</label>
              <input type="email" className="field-input mt-1" required value={form.email} onChange={onChange("email")} data-testid="contact-email" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.contact.role}</label>
            <select className="field-input mt-1" value={form.role} onChange={onChange("role")} data-testid="contact-role">
              <option value="">—</option>
              {t.contact.roles.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
          <div className="mt-4">
            <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.contact.contribution}</label>
            <select className="field-input mt-1" value={form.contribution} onChange={onChange("contribution")} data-testid="contact-contribution">
              <option value="">—</option>
              {t.contact.contributions.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
          <div className="mt-4">
            <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.contact.message}</label>
            <textarea rows={5} className="field-input mt-1" required value={form.message} onChange={onChange("message")} data-testid="contact-message" />
          </div>
          {status.error && <p className="text-red-600 text-sm mt-3" data-testid="contact-error">{status.error}</p>}
          {status.ok && <p className="text-teal text-sm mt-3 font-semibold" data-testid="contact-success">{t.contact.sent}</p>}
          <button type="submit" disabled={status.loading} className="btn-orange mt-6" data-testid="contact-submit">
            {status.loading ? "…" : t.contact.send}
          </button>
        </form>
      </div>
    </section>
  );
};

const FinalCta = () => {
  const { t } = useI18n();
  const { open: openRegister } = useRegisterModal();
  return (
    <section className="bg-teal text-white">
      <div className="container-tight py-16 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h3 className="font-display text-3xl md:text-4xl leading-tight">{t.why.title}</h3>
          <p className="text-white/75 mt-2">{t.why.subtitle}</p>
        </div>
        <button type="button" onClick={openRegister} className="btn-orange shrink-0" data-testid="final-cta">
          {t.hero.ctaPrimary} <ArrowRight size={16} />
        </button>
      </div>
    </section>
  );
};

const ImpactStrip = () => {
  const { t } = useI18n();
  const r = t.redApoyo;
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/public-stats`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setStats(data))
      .catch(() => {});
  }, []);

  // Hide entirely if no bookings yet (so we don't show "0 hours donated" awkwardly)
  if (!stats || (stats.bookings_total || 0) === 0) return null;

  const items = [
    { value: stats.total_hours, label: r.impactHours, color: "text-orange" },
    { value: stats.users_helped, label: r.impactPeople, color: "text-white" },
    { value: stats.active_professionals, label: r.impactPros, color: "text-orange" },
    { value: stats.open_slots_now, label: r.impactOpen, color: "text-white" },
  ];

  return (
    <section className="bg-teal-deep relative overflow-hidden" data-testid="impact-strip">
      <span className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-orange/15 blur-2xl pointer-events-none" />
      <span className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-orange/10 blur-2xl pointer-events-none" />
      <div className="container-tight py-16 relative">
        <div className="text-center max-w-2xl mx-auto">
          <p className="eyebrow text-orange">{r.impactEyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-tight mt-3">{r.impactTitle}</h2>
          <p className="text-white/75 mt-3">{r.impactSubtitle}</p>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.label} className="text-center bg-white/5 rounded-2xl border border-white/10 p-6 backdrop-blur-sm" data-testid={`impact-${it.label.replace(/\s+/g, '-')}`}>
              <div className={`font-display text-5xl md:text-6xl leading-none ${it.color}`}>{it.value}</div>
              <div className="text-xs md:text-sm uppercase tracking-wider text-white/70 mt-3 font-bold">{it.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/red-apoyo" className="inline-flex items-center gap-2 bg-orange hover:bg-orange/90 text-white px-6 py-3 rounded-full font-bold uppercase tracking-wider text-sm shadow-xl shadow-orange/30 transition-all hover:gap-3" data-testid="impact-cta">
            <HeartHandshake size={16} /> {r.impactCta}
          </Link>
        </div>
      </div>
    </section>
  );
};

const MarqueeRibbon = () => {
  const { t } = useI18n();
  const items = t.community.marquee || [];
  const row = [...items, ...items, ...items];
  return (
    <div className="bg-orange overflow-hidden py-3" data-testid="marquee-ribbon" aria-hidden>
      <div className="flex whitespace-nowrap marquee-track w-max">
        {row.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-6 mx-6 text-white font-display font-bold uppercase tracking-[0.22em] text-sm">
            {w} <span className="w-1.5 h-1.5 rounded-full bg-white/60 inline-block" />
          </span>
        ))}
      </div>
    </div>
  );
};

const CommunityHome = () => {
  const { t } = useI18n();
  const c = t.community;
  return (
    <section className="bg-teal-deep relative overflow-hidden" data-testid="community-home-section">
      <span aria-hidden className="absolute -top-28 -left-28 w-80 h-80 rounded-full bg-orange/15 blur-3xl pointer-events-none" />
      <span aria-hidden className="absolute -bottom-28 -right-28 w-96 h-96 rounded-full bg-orange/10 blur-3xl pointer-events-none" />
      <div className="container-tight relative grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center py-16 md:py-24">
        <div>
          <p className="eyebrow text-orange">{c.homeEyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl text-cream leading-tight mt-3" style={{ color: "#FBF6EF" }}>
            {c.homeTitle}
          </h2>
          <p className="text-white/75 mt-5 text-lg leading-relaxed max-w-xl">{c.homeSub}</p>
          <ul className="mt-7 space-y-3">
            {[c.homeB1, c.homeB2, c.homeB3].map((b) => (
              <li key={b} className="flex items-center gap-3 text-white/90 text-base font-semibold">
                <span className="w-7 h-7 rounded-lg bg-orange/20 text-orange flex items-center justify-center shrink-0">
                  <Check size={15} />
                </span>
                {b}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/user" className="btn-orange" data-testid="community-home-cta">
              {c.homeCta} <ArrowRight size={16} />
            </Link>
            <span className="text-white/60 text-xs font-bold uppercase tracking-wider">{c.homeFree}</span>
          </div>
        </div>

        <div className="relative">
          <div className="rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/10 rotate-1 hover:rotate-0 transition-transform duration-500">
            <img src={COMMUNITY_IMG} alt="" className="w-full h-[380px] md:h-[440px] object-cover" loading="lazy" />
          </div>
          <div className="absolute -top-4 -left-4 md:-left-8 bg-white rounded-xl shadow-2xl px-4 py-3 animate-float max-w-[240px]">
            <div className="flex items-center gap-1 text-orange">
              {[0, 1, 2, 3, 4].map((i) => (<Star key={i} size={12} fill="currentColor" strokeWidth={0} />))}
            </div>
            <p className="text-teal-deep text-xs font-semibold mt-1.5 leading-snug">{c.homeCard1}</p>
            <p className="text-teal-soft text-[10px] mt-1">{c.homeCard1By}</p>
          </div>
          <div className="absolute -bottom-4 right-4 md:-right-6 bg-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-float-delay">
            <span className="w-9 h-9 rounded-lg bg-teal/10 text-teal flex items-center justify-center">
              <Sparkles size={16} />
            </span>
            <p className="text-teal-deep text-xs font-bold">{c.homeCard2}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <Hero />
    <MarqueeRibbon />
    <ServiceStrip />
    <CommunityHome />
    <ImpactStrip />
    <WhyJoin />
    <FeaturedDirectory />
    <About />
    <FinalCta />
    <ContactSection />
  </>
);

export default Home;
